<template>
  <div
    v-if="isMounted"
    :class="classes"
    :style="{ backgroundColor, ...styles, ...colors }"
    role="tablist"
    aria-label="Tabs"
    aria-description="Registerkarten zur Navigation zwischen Abschnitten"
  >
    <div class="base-tabs--container">
      <slot name="leftControl" />
      <span
        v-if="controls && !hideTabs && controlLeftActive"
        key="control-left"
        class="base-tabs--control-left accessibility-outline outline-inset"
        role="button"
        tabindex="0"
        aria-label="Vorheriger Tab"
        aria-description="Zum vorherigen Tab wechseln"
        :style="{ visibility: currentPos !== 0 || endless ? 'visible' : 'hidden' }"
        @click="update(value, -1)"
        @keydown.enter.prevent="update(value, -1)"
        @keydown.space.prevent="update(value, -1)"
      >
        <i class="fal fa-chevron-left" />
      </span>
      <div
        v-if="!hideTabs"
        class="base-tabs--holder"
      >
        <div
          ref="tabs"
          class="base-tabs--tabs"
        >
          <span
            v-if="sliderStyle !== 'none'"
            class="base-tabs--slider"
          />
          <div
            v-for="item in items"
            :key="item.value"
            :ref="item.value"
            class="accessibility-outline outline-inset"
            :class="{ 'base-tabs--tab': true, active: value === item.value }"
            role="tab"
            :aria-label="`Tab ${item.label}`"
            :aria-selected="value === item.value"
            :tabindex="0"
            :aria-description="`Aktiviere Tab ${item.label}`"
            @click="tabControlActive ? update(item.value) : null"
            @keydown.enter.prevent="tabControlActive ? update(item.value) : null"
            @keydown.space.prevent="tabControlActive ? update(item.value) : null"
          >
            <BaseIcon v-if="item.icon" :icon="item.icon" class="pr-2" />
            <span>{{ item.label }}</span>
          </div>
        </div>
      </div>
      <span
        v-if="controls && !hideTabs && controlRightActive"
        key="control-right"
        class="base-tabs--control-right accessibility-outline outline-inset"
        role="button"
        tabindex="0"
        aria-label="Nächster Tab"
        aria-description="Zum nächsten Tab wechseln"
        :style="{ visibility: currentPos < items.length - 1 || endless ? 'visible' : 'hidden' }"
        @click="update(value, 1)"
        @keydown.enter.prevent="update(value, 1)"
        @keydown.space.prevent="update(value, 1)"
      >
        <i class="fal fa-chevron-right" />
      </span>
    </div>
    <slot />
    <template
      v-for="item in items"
    >
      <div
        v-if="!solo"
        v-show="item.value === value"
        :key="item.value"
        class="base-tabs--content"
        role="tabpanel"
        :aria-labelledby="item.value"
      >
        <slot :name="item.value" />
      </div>
    </template>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'BaseTabs',
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => ([]),
    },
    solo: {
      type: Boolean,
      default: false,
    },
    highlight: {
      type: Boolean,
      default: false,
    },
    duration: {
      type: Number,
      default: 500,
    },
    sticky: {
      type: Boolean,
      default: false,
    },
    top: {
      type: String,
      default: '0',
    },
    endless: {
      type: Boolean,
      default: false,
    },
    controls: {
      type: Boolean,
      default: true,
    },
    backgroundColor: {
      type: String,
      default: 'transparent',
    },
    tabColor: {
      type: [Boolean, String],
      default: false,
    },
    activeFontColor: {
      type: [Boolean, String],
      default: false,
    },
    sliderColor: {
      type: [Boolean, String],
      default: 'company-main-primary',
    },
    contentColor: {
      type: [Boolean, String],
      default: false,
    },
    sliderStyle: {
      type: String,
      default: 'bar',
      validator(value) {
        return ['bar', 'filled', 'none'].includes(value);
      },
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    elevation: {
      type: Boolean,
      default: false,
    },
    updateDelay: {
      type: Number,
      default: 0,
    },
    hideTabs: {
      type: Boolean,
      default: false,
    },
    single: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    medium: {
      type: Boolean,
      default: false,
    },
    initialUpdate: {
      type: Number,
      default: 0,
    },
    controlLeftActive: {
      type: Boolean,
      default: true,
    },
    controlRightActive: {
      type: Boolean,
      default: true,
    },
    tabControlActive: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isMounted: false,
      refs: null,
      scrollPos: 0,
      currentPos: 0,
      itemWidth: 0,
      itemOffset: 0,
    };
  },
  computed: {
    hasSlotControlLeft() {
      return !!this.$slots.leftControl;
    },
    styles() {
      const getLayout = () => {
        if (this.controls && this.controlLeftActive && this.controlRightActive) {
          return '2rem 1fr 2rem';
        }
        if (this.controls && this.controlLeftActive) {
          return '2rem 1fr';
        }
        if (this.controls && this.controlRightActive) {
          return '1fr 2rem';
        }
        return '1fr';
      };
      let layout = getLayout();

      if (this.hasSlotControlLeft) {
        layout = `auto ${layout}`;
      }

      return {
        '--base-tabs-position': this.sticky ? 'sticky' : 'relative',
        '--base-tabs-top': this.top || '0',
        '--column-count': this.items.length,
        '--item-width': this.fullWidth ? '1fr' : 'max-content',
        '--active-item-width': `${this.activeItemWidth}px`,
        '--active-item-offset': `${this.activeItemOffset}px`,
        '--scroll-position': `translateX(${this.scrollPos}px)`,
        '--scroll-duration': `${this.duration}ms`,
        '--container-grid-layout': layout,
      };
    },
    colors() {
      const areas = ['tabColor', 'sliderColor', 'contentColor', 'activeFontColor'];
      return areas.reduce((acc, prop) => {
        if (_.isBoolean(this.filled)) {
          acc[`--${_.kebabCase(this.$options.name)}--${_.kebabCase(prop)}`] = 'var(--company-main-primary)';
        } else if (_.isString(this[prop])) {
          acc[`--${_.kebabCase(this.$options.name)}--${_.kebabCase(prop)}`] = this[prop].includes('#')
            ? this[prop]
            : `var(--${this[prop]})`;
        } else {
          acc[`--${_.kebabCase(this.$options.name)}--${_.kebabCase(prop)}`] = '';
        }
        return acc;
      }, {});
    },
    classes() {
      return {
        'base-tabs': true,
        highlight: this.highlight,
        'controls-disabled': !this.controls,
        'base-tabs--elevation': this.elevation,
        'base-tabs--medium': this.medium,
        'base-tabs--small': this.small,
        [`base-tabs--slider-${this.sliderStyle}`]: this.sliderStyle !== 'none',
      };
    },
    activeItemWidth: {
      get() {
        return this.itemWidth;
      },
      set(val) {
        if (this.single) {
          this.$refs.tabs.parentElement.style.width = `${val}px`;
        }
        this.itemWidth = val;
      },
    },
    activeItemOffset: {
      get() {
        return this.itemOffset;
      },
      set(val) {
        this.itemOffset = val;
      },
    },
  },
  mounted() {
    this.refs = this.$refs;

    this.isMounted = true;
    window.addEventListener('resize', this.scrollTo);

    if (this.initialUpdate) {
      setTimeout(() => {
        this.scrollTo(this.value);
      }, this.initialUpdate);
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.scrollTo);
  },
  updated() {
    this.$nextTick(() => {
      this.scrollTo(this.value);
    });
  },
  methods: {
    update(val, add = null) {
      if (add === null) {
        this.scrollTo(val, false);
        this.currentPos = this.getIndex(val);
        this.$emit('update', val);
      } else {
        let minTo = 0;
        let maxTo = this.items.length - 1;
        if (this.endless) {
          minTo = this.items.length - 1;
          maxTo = 0;
        }

        const index = this.clamp(this.getIndex(val) + add, minTo, maxTo);

        this.scrollTo(this.items[index].value);
        this.currentPos = this.getIndex(this.items[index].value);
        this.$emit('update', this.items[index].value);
      }
    },
    clamp(value, minTo, maxTo) {
      if (value < 0) {
        return minTo;
      }
      if (value > this.items.length - 1) {
        return maxTo;
      }
      return value;
    },
    getIndex(val) {
      return this.items.findIndex((x) => x.value === val);
    },
    scrollTo(val, useDelay = true) {
      const scrollHandler = () => {
        if (val instanceof Event) {
        // eslint-disable-next-line no-param-reassign
          val = this.value;
        }

        if (this.refs?.[val]?.length) {
          const tabPos = this.refs[val][0].offsetLeft + (this.refs[val][0].offsetWidth / 2);
          const { tabs } = this.refs;
          const middle = tabs.offsetWidth / 2;
          const overflow = tabs.offsetWidth - tabs.scrollWidth;
          const result = middle - tabPos;
          let scroll = result;
          if (result > 0) {
            scroll = 0;
          } else if (result < overflow) {
            scroll = overflow;
          } else {
            scroll = result;
          }

          this.activeItemWidth = this.refs[val][0].offsetWidth;
          this.activeItemOffset = this.refs[val][0].offsetLeft;
          this.scrollPos = scroll;

          return scroll;
        }
        return 0;
      };

      if (useDelay && this.updateDelay) {
        setTimeout(() => {
          scrollHandler();
        }, this.updateDelay);
      } else {
        scrollHandler();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.base-tabs {
    position: var(--base-tabs-position);
    top: var(--base-tabs-top);
    border-radius: 5px;
    z-index: 10;

    &--elevation {
        box-shadow: 0px 0px 8px #d2d2d2;
    }

    &--container {
        display: grid;
        grid-template-columns: var(--container-grid-layout);
    }

    &--control-left,
    &--control-right {
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;

        &:hover {
            background-color: var(--company-main-primary_hover);
        }
    }

    &--holder {
        overflow: hidden;
        position: relative;
        border-radius: 5px 5px 0 0;
    }

    &--tabs {
        transition: all var(--scroll-duration) ease-in-out;
        transform: var(--scroll-position);
        display: grid;
        grid-template-columns: repeat(var(--column-count), var(--item-width));

        &.base-tabs--slider-bar,
        &.base-tabs--slider-filled {
            border-bottom: 1px solid #EAEAEA;
        }
    }

    &--medium {
        .base-tabs--tab {
            padding: 0.5rem 1.25rem;
        }
    }

    &--small {
      .base-tabs--tab {
        padding: 0.5rem 1.25rem;
        font-size: 0.875rem;
      }

      .base-tabs--tab.active {
        font-size: 0.875rem;
      }
    }

    &--tab {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        font-size: clamp(16px, 1rem, 32px);
        padding: 1em 1.25em;
        font-weight: 400;
        position: relative;
        user-select: none;
        line-height: 1;
        white-space: nowrap;
        background-color: var(--base-tabs--tab-color);

        > span {
          z-index: 1;
        }

        &:hover {
          color: var(--company-main-primary_hover);
        }

        &.active {
            cursor: default;
            font-weight: 700;
            font-size: 1em;
            color: var(--base-tabs--active-font-color);
        }
    }

    &.highlight {
        .base-tabs--tab:hover {
            &.active {
                background-color: var(--company-main-primary);
                cursor: default;
            }
        }
    }

    &--slider {
        position: absolute;
        transition: all var(--scroll-duration) ease-in-out;
        width: var(--active-item-width);
        left: var(--active-item-offset);
        height: 3px;
        background-color: var(--base-tabs--slider-color);
        top: 100%;
        transform: translateY(-50%);
        border-radius: 3px;
        z-index: 1;
    }

    &--slider-filled {
        .base-tabs--slider {
            top: 0;
            height: 100%;
            transform: none;
            border-radius: 0;
        }

        .base-tabs--tabs {
            border-bottom: none;
            background-color: var(--base-tabs--slider-color);
            gap: 1px;
        }
    }

    &--content {
        background-color: var(--base-tabs--content-color);
        border-radius: 0 0 5px 5px;
    }
}
</style>
